import { Auth0Region } from '@auth/types';
import { AppState, Auth0Provider } from '@auth0/auth0-react';
import { ReactNode, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { getAuth0ConfigForRegion } from './getAuth0ConfigForRegion';
import { retrieveRedirectUrl } from './redirectUrl';

export type Auth0ProviderWithRedirectProps = {
  region: Auth0Region;
  children: ReactNode;
};

const getStoredOrg = (
  region: string,
  storedRegion: string | null,
  storedUserOrgConfirmation: string | null,
  storedOrg: string | null,
) => {
  const validSessionConfirmed =
    storedRegion === region && storedUserOrgConfirmation === 'confirmedByUser' && storedOrg?.length;
  if (validSessionConfirmed) {
    return storedOrg;
  }
  return undefined;
};

export function Auth0ProviderWithRedirect({ region, children }: Auth0ProviderWithRedirectProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const storedOrg = localStorage.getItem('auth0OrgId');
  const storedRegion = localStorage.getItem('auth0Region');
  const storedUserOrgConfirmation = localStorage.getItem('orgIdStatus');

  // This allows us to get the user to the page they wanted to go to in the scenario
  // that they were not yet logged in.
  // See https://auth0.com/blog/complete-guide-to-react-user-authentication/
  // This implementation uses the recommendation of only passing an opaque id
  // as the returnTo value.
  // See https://community.auth0.com/t/allowed-callback-urls-ending-with-a-wildcard/7626/2
  const onRedirectCallback = useCallback(
    (appState: AppState | undefined) => {
      const to = retrieveRedirectUrl(appState) || location.pathname;
      navigate(to, { replace: true });
    },
    [navigate, location],
  );

  // There's a possibility of a race condition between onRedirectCallback and
  // getTokenSilently. Make sure the latter is never called before the former.
  // https://github.com/auth0/auth0-spa-js/issues/623#issuecomment-721209332

  return (
    <Auth0Provider
      redirectUri={`${globalThis.location.origin}/${region}`}
      useRefreshTokens
      cacheLocation="localstorage"
      onRedirectCallback={onRedirectCallback}
      // Check if the selected region (via url) has changed from the stored region
      organization={getStoredOrg(region, storedRegion, storedUserOrgConfirmation, storedOrg)}
      {...getAuth0ConfigForRegion(region)}
    >
      {children}
    </Auth0Provider>
  );
}
